import { reduce } from 'lodash'
import type { ServerLDFlagSet } from 'types/launchdarkly-node-server-sdk'
import type { ClientLDFlagSet } from 'types/launchdarkly-js-client-sdk'

/**
 * @description This file defines which LaunchDarkly flags the client should listen to.
 *
 * Flag Management:
 * 1. Adding a new flag:
 *    - Create the flag in LaunchDarkly dashboard
 *    - Add the flag key to the `flags` array below (in its original form as known to LaunchDarkly)
 *    - Run `pnpm ld-codegen` to regenerate types
 *
 * 2. Removing a flag:
 *    - Remove the flag key from the `flags` array below
 *    - Run `pnpm ld-codegen` to regenerate types
 *    - Run `pnpm ts` to verify there is no remaining references to the flag in the codebase
 *
 * Note: Flag keys must be in their original form as known to LaunchDarkly (not camelCased).
 * Run `pnpm ld-codegen:verbose` to see detailed information about flag usage and potential issues.
 */
export const flags = reduce<keyof ServerLDFlagSet, ServerLDFlagSet>(
  [
    'accounts-receivable',
    'adjustment-log-report',
    'cin-7-visibility',
    'disable-channel-forecast-break-down',
    'disable-xero-2-way-sync',
    'discrepancy-threshold-rules',
    'hide-2-way-xero-sync',
    'hide-xero',
    'ignore-landed-costs-upsell',
    'import-purchase-orders-from-qbo',
    'lockbox-reporting',
    'manually_push_skus_to_wms',
    'matching-feedback-collection',
    'mui-theme',
    'navattic-product-tour',
    'on-demand-v2-v3-pricing-migration',
    'payments-page-backfill-epoch',
    'show-linked-documents-discrepancies-counter',
    'sync-receivable-invoices-qbo',
    'trackstar-sandbox-integration',
    'use-shopify-app-store-connect-link',
    'vendor-financing',
    'require-accounting-account-for-external-bill-payments',
    'ai-command-bar',
    'push-inventory-to-shopify', // TODO: https://linear.app/settle/issue/FRONT-12476/remove-push-inventory-to-shopify-ff
    'activation-journeys-refresh', // TODO: https://linear.app/settle/issue/FRONT-12181/remove-activation-journeys-refresh-feature-flag-references
    'enable-payment-method-change',
    'move-catalog-and-grns', // TODO: https://linear.app/settle/issue/FRONT-12329/remove-move-catalog-and-grns-ff
    'move-bills-payments-and-pos', // TODO: https://linear.app/settle/issue/FRONT-12507/remove-move-bills-payments-and-pos-feature-flag-references
    'o2c-invoices',
    'apparel-view-on-po-settings', // TODO: https://linear.app/settle/issue/FRONT-12785/remove-apparel-view-on-po-settings-ff-usage-on-the-fe
    'firm-clients-saa-s-autocharge',
    'metabase-reports-in-ui', // https://linear.app/settle/issue/GRE-189/remove-metabase-reports-in-ui-feature-flag-references
    'catalog-upload-via-csv', // https://linear.app/settle/issue/GRE-207/remove-catalog-upload-via-csv-feature-flag-references
    'manufacturing-cost-on-wo', // https://linear.app/settle/issue/GRE-244/remove-manufacturing-cost-on-wo-feature-flag
    'component-cost-on-wo', // https://linear.app/settle/issue/GRE-250/remove-component-cost-on-wo-feature-flag
    'end-of-month-inventory-details-report', // https://linear.app/settle/issue/GRE-274/remove-end-of-month-inventory-details-report-feature-flag-usage-from
    'wc-utilization-widget', //https://linear.app/settle/issue/BLU-70/remove-wc-utilization-widget-ff
  ],
  (flags, flag) => ({ ...flags, [flag]: undefined }),
  {},
) as ClientLDFlagSet
